import imageCompression from 'browser-image-compression'

/**
 * HEIC/HEIF → JPEG 変換そのものの失敗を表すエラー。
 * heic2any の reject、または変換タイムアウト時に投げられる。
 *
 * 呼び出し側は `err instanceof HeicConversionError` で
 * 「HEIC変換が原因の失敗」と「それ以外の圧縮失敗」を区別できる。
 *
 * 注意: `instanceof` を成立させるため、本クラスの import 元は
 * `@modules/utils/imageCompression` に一本化すること（重複インスタンス化で false になる）。
 */
export class HeicConversionError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message)
    this.name = 'HeicConversionError'
    if (options?.cause !== undefined) {
      this.cause = options.cause
    }
  }
}

/**
 * HEIC変換のタイムアウト（ミリ秒）。
 * 高解像度HEIC（48MP等）をWebWorkerでデコードすると10秒超もあり得るため、
 * 正常変換を誤って打ち切らない範囲で無限ローディングを防ぐ値として30秒を採用。
 */
const HEIC_CONVERSION_TIMEOUT_MS = 30000

export interface ImageCompressionOptions {
  /**
   * 最大ファイルサイズ（MB単位）
   * @default 1
   */
  maxSizeMB?: number
  /**
   * 最大幅または高さ（ピクセル）
   * @default 2048
   */
  maxWidthOrHeight?: number
  /**
   * JPEG品質（0-1）
   * @default 1
   */
  quality?: number
  /**
   * Web Workerを使用するか
   * @default true
   */
  useWebWorker?: boolean
}

const DEFAULT_OPTIONS: Required<ImageCompressionOptions> = {
  maxSizeMB: 1,
  maxWidthOrHeight: 2048,
  quality: 1,
  useWebWorker: true,
}

/**
 * ファイルがHEIC/HEIF形式かどうかを判定する（MIMEタイプ or 拡張子）。
 * iOSでは file.type が空になることがあるため拡張子もチェックする。
 */
export const isHeicFile = (file: File): boolean => {
  const lowerName = file.name.toLowerCase()
  return (
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    lowerName.endsWith('.heic') ||
    lowerName.endsWith('.heif')
  )
}

/**
 * 画像ファイルを圧縮する
 * HEIC/HEIF形式の場合は自動的にJPEGに変換する
 */
export const compressImage = async (
  file: File,
  options: ImageCompressionOptions = {},
): Promise<File> => {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options }

  try {
    let fileToCompress: File = file

    // HEIC/HEIF形式の検出と変換
    if (isHeicFile(file)) {
      // HEIC変換は独立して try/catch し、失敗時は HeicConversionError として識別可能にする。
      // タイムアウトを設けて、heic2any が特定のHEICで無限に停止するケースを防ぐ。
      let timeoutId: ReturnType<typeof setTimeout> | undefined
      try {
        // 動的インポートでブラウザでのみheic2anyを読み込み
        const { default: heic2any } = await import('heic2any')

        const convertedBlob = await Promise.race([
          heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: mergedOptions.quality,
          }),
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new HeicConversionError('HEIC画像の変換がタイムアウトしました')),
              HEIC_CONVERSION_TIMEOUT_MS,
            )
          }),
        ])

        const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob
        fileToCompress = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
          type: 'image/jpeg',
          lastModified: Date.now(),
        })
      } catch (heicError) {
        if (heicError instanceof HeicConversionError) {
          throw heicError
        }
        throw new HeicConversionError('HEIC画像の変換に失敗しました', { cause: heicError })
      } finally {
        if (timeoutId !== undefined) clearTimeout(timeoutId)
      }
    }

    // 画像圧縮オプション
    const compressionOptions = {
      maxSizeMB: mergedOptions.maxSizeMB,
      maxWidthOrHeight: mergedOptions.maxWidthOrHeight,
      useWebWorker: mergedOptions.useWebWorker,
      fileType: fileToCompress.type as 'image/jpeg' | 'image/png' | 'image/webp',
    }

    const compressedFile = await imageCompression(fileToCompress, compressionOptions)

    // Fileオブジェクトとして返す
    const finalFile = new File([compressedFile], fileToCompress.name, {
      type: compressedFile.type,
      lastModified: Date.now(),
    })

    return finalFile
  } catch (error) {
    console.error('Image compression failed:', error)
    // HEIC変換失敗は型を保持して再throw（呼び出し側で区別できるようにする）
    if (error instanceof HeicConversionError) {
      throw error
    }
    throw new Error('画像の圧縮に失敗しました', { cause: error })
  }
}

/**
 * 複数の画像ファイルを圧縮する
 */
export const compressMultipleImages = async (
  files: File[],
  options: ImageCompressionOptions = {},
): Promise<File[]> => {
  return Promise.all(files.map(file => compressImage(file, options)))
}

/**
 * ファイルが画像ファイルかどうかを判定する
 */
export const isImageFile = (file: File): boolean => {
  const imageTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/heic',
    'image/heif',
  ]
  return (
    imageTypes.includes(file.type) ||
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif')
  )
}

/**
 * ファイルサイズをKB単位で取得する
 */
export const getFileSizeInKB = (file: File): number => {
  return Math.round(file.size / 1024)
}

/**
 * ファイルサイズをMB単位で取得する
 */
export const getFileSizeInMB = (file: File): number => {
  return Math.round((file.size / 1024 / 1024) * 100) / 100
}
